import { z } from 'zod/v4';
import { createDefinitionSchema } from '../utils/helpers';
import { durationSchema } from '../utils/time';
import { SeriesNameType } from './shared';

export type PrometheusQueryType = z.infer<typeof prometheusQueryTypeSchema>;
const prometheusQueryTypeSchema = z.enum(['Instant', 'Range']);

export type PrometheusQueryReducer = z.infer<typeof prometheusReducerSchema>;
const prometheusReducerSchema = z.enum(['First', 'Last', 'FirstNumber', 'LastNumber', 'Avg', 'Sum', 'Min', 'Max']);

export const prometheusTruncationStrategySchema = z.enum(['Off', 'Max', 'Min', 'Avg']);
export type PrometheusTruncationStrategy = z.infer<typeof prometheusTruncationStrategySchema>;
export const isValidTruncationStrategy = (value: string): value is PrometheusTruncationStrategy => {
  return prometheusTruncationStrategySchema.safeParse(value).success;
};

export type PrometheusQuerySpec = z.infer<typeof prometheusQuerySpecSchema>;
const prometheusQuerySpecSchema = z.object({
  query: z.string(),
  /**
   * DEPRECATED
   *
   * Use series_name instead of series_name_format.
   * If both fields are included, we use series_name.
   *
   * TODO: Migrate to series_name.
   */
  series_name_format: z.string().optional(),
  series_name: z
    .discriminatedUnion('type', [
      z.object({
        type: z.literal(SeriesNameType.Labels),
        name_pattern: z.string().optional(),
      }),
      z.object({
        type: z.literal(SeriesNameType.Regex),
        regex: z.string().optional(),
        name_pattern: z.string().optional(),
      }),
    ])
    .optional(),
  min_step: durationSchema.optional(),
  resolution: z.number().optional(),
  disable_duplicate_series_notice: z.boolean().optional(),
  queryType: prometheusQueryTypeSchema.optional(),
  reducer: prometheusReducerSchema.optional(),
  truncation_strategy: prometheusTruncationStrategySchema.optional(),
  downsampling_strategy: z.enum(['Avg', 'Sum', 'Min', 'Max', 'LTTB']).optional(),
});

export type PrometheusTimeSeriesQueryDefinition = z.infer<typeof prometheusTimeSeriesQueryDefinitionSchema>;
export const prometheusTimeSeriesQueryDefinitionSchema = createDefinitionSchema(
  'PrometheusTimeSeriesQuery',
  prometheusQuerySpecSchema
);

export type PrometheusEventQuerySpec = z.infer<typeof prometheusEventQuerySpecSchema>;
const prometheusEventQuerySpecSchema = z.object({
  query: z.string(),
  title_format: z.string().optional(),
  min_step: durationSchema.optional(),
  step: durationSchema.optional(),
});

export type PrometheusEventQueryDefinition = z.infer<typeof prometheusEventQueryDefinitionSchema>;
export const prometheusEventQueryDefinitionSchema = createDefinitionSchema(
  'PrometheusEventQuery',
  prometheusEventQuerySpecSchema
);

export type PrometheusLabelValuesVariableOptions = z.infer<typeof prometheusLabelValuesVariableSchema>;
const prometheusLabelValuesVariableSchema = z.object({
  label_name: z.string(),
  matchers: z.array(z.string()).optional(),
});

export const prometheusLabelValuesVariableDefinitionSchema = createDefinitionSchema(
  'PrometheusLabelValuesVariable',
  prometheusLabelValuesVariableSchema
);

export type PrometheusLabelNamesVariableOptions = z.infer<typeof prometheusLabelNamesVariableSchema>;
const prometheusLabelNamesVariableSchema = z.object({
  matchers: z.array(z.string()).optional(),
});

export const prometheusLabelNamesVariableDefinitionSchema = createDefinitionSchema(
  'PrometheusLabelNamesVariable',
  prometheusLabelNamesVariableSchema
);

export type PrometheusPromQLVariableOptions = z.infer<typeof prometheusPromQLVariableOptionsSchema>;
const prometheusPromQLVariableOptionsSchema = z.object({
  // expr is the PromQL expression.
  expr: z.string(),
  // label_name is the name of the label that will be used after the PromQL query is performed to select the label value.
  // Note: This field is not part of the Prometheus API.
  label_name: z.string(),

  query_type: prometheusQueryTypeSchema.optional(),
});

export const prometheusPromQLVariableDefinitionSchema = createDefinitionSchema(
  'PrometheusPromQLVariable',
  prometheusPromQLVariableOptionsSchema
);

export type PrometheusTimeSeriesQueryKind = PrometheusTimeSeriesQueryDefinition['kind'];
